(() => {
  const pageSize = 3;
  const requestPageSize = 30;
  const openDialogs = 'dialog[data-review-dialog][open], dialog[data-review-media-dialog][open]';
  const makeText = (tag, value) => {
    const element = document.createElement(tag);
    element.textContent = value;
    return element;
  };
  const initialize = () => document.querySelectorAll('[data-customer-review-group]').forEach((group) => {
    if (group.dataset.initialized) return;
    const dialog = group.closest('[data-review-dialog]');
    if (typeof dialog?.showModal !== 'function') return;
    group.dataset.initialized = 'true';
    const card = dialog.closest('.review-customer-feedback__model');
    const opener = card.querySelector('[data-review-open]');
    const close = dialog.querySelector('[data-review-close]');
    const list = group.querySelector('[data-customer-review-list]');
    const filter = group.querySelector('[data-review-filter]');
    const scroll = group.querySelector('[data-review-scroll]');
    const status = group.querySelector('[data-review-status]');
    const error = group.querySelector('[data-review-error]');
    const retry = group.querySelector('[data-review-retry]');
    const pagination = group.querySelector('[data-review-pagination]');
    const previous = group.querySelector('[data-review-prev]');
    const next = group.querySelector('[data-review-next]');
    const pageLabel = group.querySelector('[data-review-page]');
    let disposed = false;
    let reviews = [];
    let currentPage = 0;
    let ready = false;
    let loading = false;
    let failed = false;
    let lastTrigger = opener;
    const label = (name, values = {}) => Object.entries(values).reduce(
      (text, [key, value]) => text.replace(`__${key}__`, String(value)), group.dataset[name] || '',
    );
    const render = () => {
      if (disposed) return;
      const expanded = new Set([...list.querySelectorAll('[data-review-id]')]
        .filter((article) => article.querySelector('details')?.open)
        .map((article) => article.dataset.reviewId));
      const focusedReview = document.activeElement?.closest('[data-review-id]')?.dataset.reviewId;
      const matching = reviews.filter((review) => filter.value === 'all' || String(review.star) === filter.value);
      const pages = Math.ceil(matching.length / pageSize);
      currentPage = Math.max(0, Math.min(currentPage, pages - 1));
      const fragment = document.createDocumentFragment();
      matching.slice(currentPage * pageSize, (currentPage + 1) * pageSize).forEach((review) => {
        const article = document.createElement('article');
        article.className = 'review-customer-feedback__review';
        article.dataset.reviewId = String(review.id);
        article.append(makeText('span', `${review.star} / 5`));
        const details = document.createElement('details');
        details.open = expanded.has(String(review.id));
        const summary = document.createElement('summary');
        summary.append(makeText('h4', review.title || `${review.star} / 5`));
        details.append(summary);
        if (review.content) {
          details.append(makeText('p', review.content));
          const excerpt = makeText('p', review.content);
          excerpt.className = 'review-customer-feedback__excerpt';
          article.append(details, excerpt);
        } else article.append(details);
        const date = new Date(review.created_at);
        if (!Number.isNaN(date.getTime())) {
          const time = makeText('time', new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
            year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
          }).format(date));
          time.dateTime = date.toISOString().slice(0, 10);
          article.append(time);
        }
        fragment.append(article);
      });
      list.replaceChildren(fragment);
      if (focusedReview) [...list.querySelectorAll('[data-review-id]')]
        .find((article) => article.dataset.reviewId === focusedReview)?.querySelector('summary')?.focus({ preventScroll: true });
      pagination.hidden = pages < 2;
      previous.disabled = currentPage === 0;
      next.disabled = currentPage >= pages - 1;
      pageLabel.textContent = `${currentPage + 1} / ${Math.max(1, pages)}`;
      filter.disabled = !ready;
      if (loading) status.textContent = reviews.length
        ? label('labelLoadingMore', { COUNT: reviews.length }) : label('labelLoading');
      else if (matching.length) status.textContent = label('labelPage', {
        CURRENT: currentPage + 1, TOTAL: pages, COUNT: matching.length,
      });
      else status.textContent = ready ? label('labelEmpty') : '';
      error.hidden = !failed;
      error.textContent = failed ? label(reviews.length ? 'labelPartialError' : 'labelError') : '';
      retry.hidden = !failed;
    };
    const load = async () => {
      if (loading || ready) return;
      loading = true;
      failed = false;
      group.dataset.state = 'loading';
      list.setAttribute('aria-busy', 'true');
      render();
      const found = new Map();
      try {
        let expectedTotal;
        for (let page = 1; page <= 100; page += 1) {
          const url = new URL('https://storefront.trustshop.io/storefront/product-reviews/reviews');
          url.searchParams.set('product_id', group.dataset.productId);
          url.searchParams.set('shop_id', group.dataset.shopId);
          url.searchParams.set('filter', 'all');
          url.searchParams.set('sort', 'most_helpful');
          url.searchParams.set('per_page', String(requestPageSize));
          if (page > 1) url.searchParams.set('current_page', String(page));
          const response = await fetch(url, { credentials: 'omit', signal: AbortSignal.timeout(15000) });
          if (!response.ok) throw new Error('Review request failed');
          const data = await response.json();
          if (disposed) return;
          if (!Array.isArray(data.data) || !Number.isInteger(data.total) || data.total < 0) throw new Error('Invalid review response');
          if (expectedTotal === undefined) expectedTotal = data.total;
          if (data.total !== expectedTotal) throw new Error('Review inventory changed during loading');
          const before = found.size;
          data.data.forEach((review) => {
            if (review.id == null || !Number.isInteger(review.star) || review.star < 1 || review.star > 5) throw new Error('Invalid review');
            found.set(String(review.id), {
              id: review.id, star: review.star, title: String(review.title || ''),
              content: String(review.content || ''), created_at: review.created_at,
            });
          });
          reviews = [...found.values()];
          render();
          if (data.has_next_page === false) {
            if (found.size !== expectedTotal) throw new Error('Incomplete review inventory');
            ready = true;
            group.dataset.reviewCount = String(found.size);
            group.dataset.state = 'ready';
            return;
          }
          if (found.size <= before) throw new Error('No review pagination progress');
        }
        throw new Error('Review pagination limit reached');
      } catch {
        failed = true;
        group.dataset.state = 'error';
      } finally {
        loading = false;
        list.setAttribute('aria-busy', 'false');
        render();
      }
    };
    const open = (trigger = opener) => {
      if (!dialog.isConnected || dialog.open) return;
      document.querySelectorAll(openDialogs).forEach((other) => other.close());
      lastTrigger = trigger;
      dialog.showModal();
      document.documentElement.classList.add('review-dialog-open');
      opener.setAttribute('aria-expanded', 'true');
      dialog.querySelector('.review-customer-feedback__reader-header h3').focus({ preventScroll: true });
      void load();
    };
    opener.setAttribute('role', 'button');
    opener.setAttribute('aria-haspopup', 'dialog');
    opener.setAttribute('aria-expanded', 'false');
    opener.addEventListener('click', (event) => {
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      open(opener);
    });
    opener.addEventListener('keydown', (event) => {
      if (event.key === ' ') { event.preventDefault(); open(opener); }
    });
    close.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      const rect = dialog.getBoundingClientRect();
      if (event.target === dialog && (event.clientX < rect.left || event.clientX > rect.right
        || event.clientY < rect.top || event.clientY > rect.bottom)) dialog.close();
    });
    dialog.addEventListener('close', () => {
      opener.setAttribute('aria-expanded', 'false');
      if (!document.querySelector(openDialogs)) {
        document.documentElement.classList.remove('review-dialog-open');
        if (lastTrigger?.isConnected) lastTrigger.focus({ preventScroll: true });
      }
    });
    filter.addEventListener('change', () => {
      currentPage = 0;
      render();
      scroll.scrollTop = 0;
    });
    [previous, next].forEach((button, index) => button.addEventListener('click', () => {
      currentPage += index === 0 ? -1 : 1;
      render();
      scroll.scrollTop = 0;
      list.focus({ preventScroll: true });
    }));
    retry.addEventListener('click', () => { close.focus({ preventScroll: true }); void load(); });
    const openAnchor = () => {
      if (window.location.hash === `#${card.id}`) open();
    };
    const handleAnchorClick = (event) => {
      if (!dialog.isConnected) return;
      const anchor = event.target.closest('a[href]');
      if (!anchor || event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin === window.location.origin && url.pathname === window.location.pathname && url.hash === `#${card.id}`) {
        event.preventDefault();
        if (window.location.hash !== url.hash) window.history.pushState(null, '', url.hash);
        open(anchor);
      }
    };
    document.addEventListener('click', handleAnchorClick);
    window.addEventListener('hashchange', openAnchor);
    card.closest('.shopify-section')?.addEventListener('shopify:section:unload', () => {
      disposed = true;
      document.removeEventListener('click', handleAnchorClick);
      window.removeEventListener('hashchange', openAnchor);
      if (dialog.open) dialog.close();
      if (!document.querySelector(openDialogs)) document.documentElement.classList.remove('review-dialog-open');
    }, { once: true });
    openAnchor();
  });
  initialize();
  document.addEventListener('shopify:section:load', initialize);
})();
